/**
 * Operad-diagram primitives — leaves, hub nodes, root nodes, and the
 * three-input fan-in edge bundle.
 *
 * Every operad / cooperad / bioperad diagram in this package composes the
 * same handful of pieces so the visual grammar stays consistent across the
 * three explainer cards.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, SAFE, withAlpha } from "../../../design-system/tokens.js";

/** Period of the gentle pulse animations used in this package. */
export const ANIMATION_PERIOD_MS = 1400;

/** A single labelled input or output node. */
export interface LeafSpec {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly label: string;
}

/**
 * Props for the {@link Leaf} primitive. `safe` toggles between the neutral
 * outline rendering and the SAFE-palette filled rendering used by the
 * closure illustrations. (No `unsafe` mode is needed in this PR; if a
 * future card wants to render a vulnerable leaf, add the variant alongside
 * a test that exercises it.)
 */
export interface LeafProps {
  readonly spec: LeafSpec;
  readonly accent: string;
  readonly safe?: boolean;
}

export const ARITY3_LEAF_1: LeafSpec = { id: "leaf-1", x: 36, y: 28, label: "x₁" };
export const ARITY3_LEAF_2: LeafSpec = { id: "leaf-2", x: 96, y: 28, label: "x₂" };
export const ARITY3_LEAF_3: LeafSpec = { id: "leaf-3", x: 156, y: 28, label: "x₃" };

export const ARITY3_LEAVES: readonly LeafSpec[] = [ARITY3_LEAF_1, ARITY3_LEAF_2, ARITY3_LEAF_3];

export const ARITY3_ROOT = { x: 96, y: 132, label: "f(x₁, x₂, x₃)" } as const;
export const ARITY3_HUB = { x: 96, y: 86 } as const;

/** Three input lines fanning down into a hub, plus a line from hub to root. */
export function OperadicEdges({ accent }: { readonly accent: string }): React.JSX.Element {
  return (
    <g stroke={accent} strokeWidth={1.5} fill="none">
      {ARITY3_LEAVES.map((leaf) => (
        <line key={leaf.id} x1={leaf.x} y1={leaf.y + 6} x2={ARITY3_HUB.x} y2={ARITY3_HUB.y - 8} />
      ))}
      <line x1={ARITY3_HUB.x} y1={ARITY3_HUB.y + 8} x2={ARITY3_ROOT.x} y2={ARITY3_ROOT.y - 8} />
    </g>
  );
}

/**
 * A small labelled disc used for both operadic inputs (top) and cooperadic
 * outputs (bottom). When `safe` is true the leaf is rendered with the SAFE
 * palette so the closure animations can flow green from leaves to root.
 */
export function Leaf({ spec, accent, safe = false }: LeafProps): React.JSX.Element {
  const fill = safe ? SAFE[300] : "white";
  const stroke = safe ? SAFE[700] : accent;
  return (
    <g>
      <circle cx={spec.x} cy={spec.y} r={11} fill={fill} stroke={stroke} strokeWidth={1.5} />
      <text
        x={spec.x}
        y={spec.y + 3}
        textAnchor="middle"
        fontSize="10"
        fontFamily={FONT.mono}
        fill={GRAY[900]}
      >
        {spec.label}
      </text>
    </g>
  );
}

/** An internal node ("operation hub") drawn as a small rounded rectangle. */
export function HubNode({
  cx,
  cy,
  label,
  accent,
  safe = false,
}: {
  readonly cx: number;
  readonly cy: number;
  readonly label: string;
  readonly accent: string;
  readonly safe?: boolean;
}): React.JSX.Element {
  const fill = safe ? SAFE[100] : withAlpha(accent, "1A");
  const stroke = safe ? SAFE[700] : accent;
  return (
    <g>
      <rect
        x={cx - 18}
        y={cy - 11}
        width={36}
        height={22}
        rx={6}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.5}
      />
      <text
        x={cx}
        y={cy + 4}
        textAnchor="middle"
        fontSize="11"
        fontFamily={FONT.mono}
        fill={GRAY[900]}
      >
        {label}
      </text>
    </g>
  );
}

/** A wide labelled root rectangle for the final output of an operadic tree. */
export function RootNode({
  cx,
  cy,
  label,
  accent,
  safe = false,
}: {
  readonly cx: number;
  readonly cy: number;
  readonly label: string;
  readonly accent: string;
  readonly safe?: boolean;
}): React.JSX.Element {
  const fill = safe ? SAFE[300] : withAlpha(accent, "1A");
  const stroke = safe ? SAFE[700] : accent;
  return (
    <g>
      <rect
        x={cx - 60}
        y={cy - 11}
        width={120}
        height={22}
        rx={6}
        fill={fill}
        stroke={stroke}
        strokeWidth={1.5}
      />
      <text
        x={cx}
        y={cy + 4}
        textAnchor="middle"
        fontSize="11"
        fontFamily={FONT.mono}
        fill={GRAY[900]}
      >
        {label}
      </text>
    </g>
  );
}
